import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { EASE } from "./Reveal";
import { scrollToId } from "@/lib/api";

const HERO_IMG = "https://customer-assets-lqy194kg.emergentagent.net/job_kingdom-reach-media/artifacts/tw55vxc5_12%20Components%20of%20Gods%20Kingdom.png";

const LINES = ["YOUR CHURCH", "WAS NEVER CALLED", "TO BE"];

const STATS = [
  ["84%", "of U.S. adults use YouTube"],
  ["71%", "use Facebook"],
  ["50%", "use Instagram"],
];

const CLOUDS = [
  { top: "8%", width: 460, height: 130, duration: 95, delay: -12, opacity: 0.9 },
  { top: "26%", width: 620, height: 170, duration: 130, delay: -58, opacity: 0.65 },
  { top: "3%", width: 320, height: 100, duration: 80, delay: -34, opacity: 0.8 },
  { top: "52%", width: 700, height: 190, duration: 150, delay: -90, opacity: 0.5 },
  { top: "40%", width: 400, height: 120, duration: 105, delay: -20, opacity: 0.75 },
  { top: "68%", width: 520, height: 150, duration: 115, delay: -70, opacity: 0.55 },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/70 to-[#F3DF9F]" />
      </motion.div>
      <div className="hero-glow absolute -top-32 -right-40 w-[46rem] h-[46rem] pointer-events-none" />
      <div className="blue-glow absolute bottom-0 -left-52 w-[40rem] h-[40rem] pointer-events-none" />

      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]" data-testid="hero-clouds" aria-hidden="true">
        {CLOUDS.map((c, i) => (
          <div key={i} className="cloud"
            style={{ top: c.top, width: c.width, height: c.height, opacity: c.opacity, animationDuration: `${c.duration}s`, animationDelay: `${c.delay}s` }}>
            <span className="cloud-blob" style={{ width: "55%", height: "150%", left: "10%", top: "-45%" }} />
            <span className="cloud-blob" style={{ width: "45%", height: "125%", right: "12%", top: "-32%" }} />
          </div>
        ))}
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
          className="inline-block font-faith font-bold text-sm sm:text-base lg:text-lg tracking-[0.22em] uppercase text-[#2E1065] bg-gradient-to-r from-[#F5D76E] via-[#D4AF37] to-[#C9982A] px-6 sm:px-8 py-3 sm:py-4 rounded-full shadow-[0_8px_40px_rgba(212,175,55,0.55)] mb-8"
          data-testid="hero-eyebrow">
          Minister of Social Media™ — A Digital Ministry Revival
        </motion.p>

        <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-white"
          data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span className="block"
                initial={{ y: "112%" }} animate={{ y: 0 }}
                transition={{ delay: 0.3 + i * 0.14, duration: 0.95, ease: EASE }}>
                {line}
              </motion.span>
            </span>
          ))}
          <span className="block overflow-hidden pb-2">
            <motion.span className="block gold-text"
              initial={{ y: "112%" }} animate={{ y: 0 }}
              transition={{ delay: 0.3 + LINES.length * 0.14, duration: 0.95, ease: EASE }}>
              INVISIBLE.
            </motion.span>
          </span>
        </h1>

        <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease: EASE }}
          className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subheadline">
          Your next member may be scrolling right now. Let’s make God, the Kingdom, and your church
          <span className="text-gold font-faith"> relevant, visible, and reachable </span>again.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4">
          <button data-testid="hero-book-audit-button" onClick={() => scrollToId("audit")}
            className="rounded-full bg-gold px-8 py-4 text-xs font-bold tracking-[0.18em] uppercase text-navy transition-all hover:bg-gold-hover hover:shadow-[0_0_35px_rgba(212,175,55,0.45)] hover:scale-[1.04]">
            Book Your Digital Ministry Audit
          </button>
          <button data-testid="hero-visibility-plan-button" onClick={() => scrollToId("process")}
            className="rounded-full border border-gold/50 px-8 py-4 text-xs font-bold tracking-[0.18em] uppercase text-gold transition-all hover:bg-gold hover:text-navy hover:scale-[1.04]">
            See the 90-Day Visibility Plan
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }}
          className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="hero-stats">
          {STATS.map(([num, label]) => (
            <div key={num} className="flex items-baseline gap-2">
              <span className="font-display text-2xl text-gold">{num}</span>
              <span className="text-[11px] tracking-[0.14em] uppercase text-slate-400">{label}</span>
            </div>
          ))}
          <span className="text-[10px] tracking-[0.14em] uppercase text-slate-500">— Pew Research, 2025</span>
        </motion.div>
      </motion.div>

      <motion.button data-testid="hero-scroll-indicator" onClick={() => scrollToId("manifesto")}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gold/70 hover:text-gold transition-colors"
        aria-label="Scroll down">
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="block">
          <ChevronDown size={26} />
        </motion.span>
      </motion.button>
    </section>
  );
}
